"use client";
import type React from "react";
import { useState, useEffect, useRef } from "react";
import { AlertCircle } from "lucide-react";
import Image from "next/image";
import { useRouter, useSearchParams } from "next/navigation";
import axios from "axios";
import vLogo from "../components/images/vilva-new-logo.webp";
import dynamic from "next/dynamic";
import type ReCAPTCHABase from "react-google-recaptcha";

const ReCAPTCHA = dynamic(() => import("react-google-recaptcha"), {
  ssr: false,
  loading: () => (
    <div className="h-[78px] w-[304px] bg-gray-100 rounded animate-pulse" />
  ),
}) as typeof ReCAPTCHABase;
import toast from "react-hot-toast";
import Link from "next/link";
import Select from "react-select";
import { SigninRightSec } from "./SigninRightSec";
import { useUser } from "./userContext";
import { getFlagUrl } from "@/lib/imageUrl";
import { getApiUrl } from "@/lib/apiUrl";
import { trackEvent } from "@/lib/metaPixel";


interface FormData {
  fullName: string;
  email: string;
  businessName: string;
  country: string;
  countryCode: string;
  mobileNumber: string;
  otp: string[];
  password: string;
  confirmPassword: string;
  honeypot: string;
  reset_token?: string;
}

interface FormErrors {
  fullName?: string;
  email?: string;
  businessName?: string;
  mobileNumber?: string;
  country?: string;
  otp?: string;
  password?: string;
  confirmPassword?: string;
}

interface Country {
  id: number;
  country: string;
  short_code: string;
  calling_code: number;
  phone_length: number;
  status: number;
  flag: string;
  currency: string;
  currency_symbol: string;
}

interface CustomOption {
  value: string;
  label: string;
  flag: string;
  calling_code: string;
  country_name: string;
  phone_length: number;
}

interface VisitorData {
  city: string;
  region: string;
  country: string;
  postal: string;
  latitude: string;
  longitude: string;
  org: string;
}

export default function SignUpCard() {
  const { login, isLoggedIn, user, cartinfoData } = useUser();
  const router = useRouter();
  const [formData, setFormData] = useState<FormData>({
    fullName: "",
    email: "",
    businessName: "",
    mobileNumber: "",
    country: "",
    countryCode: "",
    otp: ["", "", "", "", "", ""],
    password: "",
    confirmPassword: "",
    honeypot: "",
    reset_token: "",
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [step, setStep] = useState<"account" | "otp" | "password">("account");
  const [recaptchaToken, setRecaptchaToken] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const otpRefs = useRef<(HTMLInputElement | null)[]>([]);
  const [isCountryLoading, setIsCountryLoading] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const recaptchaSiteKey = "6Lf9iEYqAAAAAGBkHMfKHyrXoDP3tyX_yZ8cL9b-";
  const [formFocused, setFormFocused] = useState(false);
  const key = "waSs57FdgthqfzVkv3RMrpTjBcYGKJJRQWtNxhmbS2EPpUjwy8";
  const [countries, setCountries] = useState<Country[]>([]);
  const assetscountries = getFlagUrl();
  const [visitorData, setVisitorData] = useState<VisitorData>({
    city: "Unknown",
    region: "Unknown",
    country: "Unknown",
    postal: "Unknown",
    latitude: "Unknown",
    longitude: "Unknown",
    org: "Unknown",
  });

  // Fetch countries
  useEffect(() => {
    setIsCountryLoading(true);
    fetch("/api/countries")
      .then((response) => {
        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }
        return response.json();
      })
      .then((data: Country[]) => {
        setCountries(Array.isArray(data) ? data : []);
        // Default to India immediately so the field never sits blank — the IP
        // lookup below only *upgrades* this if it resolves to a different country.
        const india = Array.isArray(data) ? data.find((c) => c.calling_code === 91) : undefined;
        if (india) {
          setFormData((prev) => ({
            ...prev,
            country: prev.country || india.id.toString(),
            countryCode: prev.countryCode || `${india.calling_code}`,
          }));
        }
        setIsCountryLoading(false);
      })
      .catch((error) => {
        console.error("Error fetching countries:", error);
        notifyError("Failed to load country codes");
        setIsCountryLoading(false);
      });
  }, []);

  // Fetch visitor data and set default country
  useEffect(() => {
    if (countries.length > 0) {
      // Server-side lookup (no CORS/rate-limit exposure in the browser — see app/api/geo).
      fetch('/api/geo')
        .then((res) => res.json())
        .then((data) => {
          setVisitorData({
            city: data.city || "Unknown",
            region: data.region || "Unknown",
            country: data.country_code || "Unknown",
            postal: data.postal || "Unknown",
            latitude: data.latitude?.toString() || "Unknown",
            longitude: data.longitude?.toString() || "Unknown",
            org: data.org || "Unknown",
          });

          // Find the country matching the short_code from IP API
          const matchingCountry = countries.find((country) => country.short_code === data.country_code);
          if (matchingCountry) {
            setFormData((prev) => ({
              ...prev,
              country: matchingCountry.id.toString(),
              countryCode: `${matchingCountry.calling_code}`,
            }));
          } else {
            // Fallback to India (+91)
            const defaultCountry = countries.find((country) => country.calling_code === 91);
            if (defaultCountry) {
              setFormData((prev) => ({
                ...prev,
                country: defaultCountry.id.toString(),
                countryCode: `${defaultCountry.calling_code}`,
              }));
            }
          }
        })
        .catch((error) => {
          console.error("Error fetching visitor data:", error);
          setVisitorData({
            city: "Unknown",
            region: "Unknown",
            country: "Unknown",
            postal: "Unknown",
            latitude: "Unknown",
            longitude: "Unknown",
            org: "Unknown",
          });
          // Fallback to India (+91) on error
          const defaultCountry = countries.find((country) => country.calling_code === 91);
          if (defaultCountry) {
            setFormData((prev) => ({
              ...prev,
              country: defaultCountry.id.toString(),
              countryCode: `${defaultCountry.calling_code}`,
            }));
          }
        });
    }
  }, [countries]);

  // AES-GCM Encryption Function
  const aesEncrypt = async (plainText: string, key: string) => {
    const encoder = new TextEncoder();
    try {
      const encodedKey = encoder.encode(key);
      const cryptoKey = await crypto.subtle.importKey(
        "raw",
        await crypto.subtle.digest("SHA-256", encodedKey),
        { name: "AES-GCM" },
        false,
        ["encrypt"]
      );
      const iv = crypto.getRandomValues(new Uint8Array(12));
      const ciphertext = await crypto.subtle.encrypt(
        { name: "AES-GCM", iv, tagLength: 128 },
        cryptoKey,
        encoder.encode(plainText)
      );
      const encryptedData = new Uint8Array([...iv, ...new Uint8Array(ciphertext)]);
      return btoa(String.fromCharCode(...encryptedData));
    } catch (err) {
      console.error("Encryption failed:", err);
      throw new Error("Encryption failed");
    }
  };

  // AES-GCM Decryption Function
  const aesDecrypt = async (encryptedData: string, key: string) => {
    const decoder = new TextDecoder();
    try {
      const data = Uint8Array.from(atob(encryptedData), (char) => char.charCodeAt(0));
      const iv = data.slice(0, 12);
      const ciphertext = data.slice(12);
      const encodedKey = new TextEncoder().encode(key);
      const cryptoKey = await crypto.subtle.importKey(
        "raw",
        await crypto.subtle.digest("SHA-256", encodedKey),
        { name: "AES-GCM" },
        false,
        ["decrypt"]
      );
      const decrypted = await crypto.subtle.decrypt(
        { name: "AES-GCM", iv, tagLength: 128 },
        cryptoKey,
        ciphertext
      );
      return decoder.decode(decrypted);
    } catch (err) {
      console.error("Decryption failed:", err);
      throw new Error("Decryption failed");
    }
  };

  // Map countries to custom options for react-select
  const customOptions: CustomOption[] = countries.map((country) => ({
    value: country.id.toString(),
    label: `${country.calling_code}`,
    flag: country.flag ? `${assetscountries}${country.flag}` : "",
    calling_code: `${country.calling_code}`,
    country_name: country.country,
    phone_length: country.phone_length,
  }));

  const formatOptionLabel = ({ calling_code, flag, country_name }: CustomOption) => (
    <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
      {flag && <img src={flag} alt={`${country_name} flag`} style={{ width: "20px" }} />}
      <span>{calling_code}</span>
    </div>
  );

  const handleCountryChange = (selectedOption: CustomOption | null) => {
    if (selectedOption) {
      setFormData((prev) => ({
        ...prev,
        country: selectedOption.value,
        countryCode: selectedOption.calling_code,
        mobileNumber: "",
      }));
      setErrors((prev) => ({ ...prev, country: undefined }));
    } else {
      setFormData((prev) => ({
        ...prev,
        country: "",
        countryCode: "",
      }));
    }
  };

  const countryCodeStyles = {
    control: (provided: any) => ({
      ...provided,
      borderRadius: "8px 0 0 8px",
      background: "white",
      fontFamily: "Latinka Regular",
      fontSize: "16px",
      color: "#090D28",
      minWidth: "100px",
      height: "42px",
      border: "1px solid #D1D7DD",
      borderRight: "none",
      outline: "none",
      display: "flex",
      alignItems: "center",
    }),
    placeholder: (provided: any) => ({
      ...provided,
      color: "#090D28",
      fontFamily: "Latinka Regular",
      fontSize: "16px",
    }),
    dropdownIndicator: (provided: any) => ({
      ...provided,
      display: "none",
    }),
    indicatorSeparator: (provided: any) => ({
      ...provided,
      display: "none",
    }),
    menuList: (provided: any) => ({
      ...provided,
      maxHeight: "300px",
      "&::-webkit-scrollbar": {
        width: "6px",
      },
      "&::-webkit-scrollbar-thumb": {
        background: "#2563eb",
        borderRadius: "3px",
      },
      "&::-webkit-scrollbar-track": {
        background: "#E6E9FF",
        borderRadius: "3px",
      },
    }),
    singleValue: (provided: any) => ({
      ...provided,
      color: "#090D28",
      fontFamily: "Latinka Regular",
      fontSize: "16px",
      display: "flex",
      alignItems: "center",
      height: "100%",
    }),
  };

  const validateEmail = (email: string): boolean => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const validateMobile = (mobile: string): boolean => {
    const selectedCountry = countries.find((country) => country.id.toString() === formData.country);
    const phoneLength = selectedCountry?.phone_length || 10;
    return new RegExp(`^\\d{${phoneLength}}$`).test(mobile.replace(/\s/g, ""));
  };

  const validatePassword = (password: string): boolean => /^.{8,}$/.test(password);

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};
    if (step === "account") {
      if (!formData.fullName.trim()) newErrors.fullName = "Full name is required";
      else if (formData.fullName.trim().length < 2) newErrors.fullName = "Full name must be at least 2 characters";
      if (!formData.email.trim()) newErrors.email = "Email is required";
      else if (!validateEmail(formData.email)) newErrors.email = "Enter valid email address";
      if (!formData.businessName.trim()) newErrors.businessName = "Business name is required";
      else if (formData.businessName.trim().length < 2) newErrors.businessName = "Business name must be at least 2 characters";
      if (!formData.mobileNumber.trim()) newErrors.mobileNumber = "Mobile number is required";
      else if (!validateMobile(formData.mobileNumber))
        newErrors.mobileNumber = `Mobile number must be ${countries.find((c) => c.id.toString() === formData.country)?.phone_length || 10} digits`;
      if (!formData.country) newErrors.country = "Country code is required";
      if (!recaptchaToken) newErrors.mobileNumber = "Please complete the reCAPTCHA";
    } else if (step === "otp") {
      if (formData.otp.join("").length !== 6) newErrors.otp = "Please enter a 6-digit OTP";
    } else if (step === "password") {
      if (!formData.password) newErrors.password = "Password is required";
      else if (!validatePassword(formData.password)) newErrors.password = "Password must be at least 8 characters";
      if (formData.password !== formData.confirmPassword) newErrors.confirmPassword = "Passwords do not match";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0 && !formData.honeypot;
  };



  const handleInputChange = (field: keyof FormData, value: string, index?: number) => {
    if (index !== undefined) {
      if (!/^\d*$/.test(value)) return;
      const newOtp = [...formData.otp];
      newOtp[index] = value;
      setFormData((prev) => ({ ...prev, otp: newOtp }));
      if (value && index < 5) otpRefs.current[index + 1]?.focus();
      if (errors.otp) setErrors((prev) => ({ ...prev, otp: undefined }));
    } else {
      setFormData((prev) => ({ ...prev, [field]: value }));
      if (errors[field as keyof FormErrors]) {
        setErrors((prev) => ({ ...prev, [field]: undefined }));
      }
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, index: number) => {
    if (e.key === "Backspace" && !formData.otp[index] && index > 0) {
      otpRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>, index: number) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
    const newOtp = [...formData.otp];
    for (let i = 0; i < pastedData.length && index + i < 6; i++) {
      newOtp[index + i] = pastedData[i];
    }
    setFormData((prev) => ({ ...prev, otp: newOtp }));
    if (index + pastedData.length < 6) {
      otpRefs.current[index + pastedData.length]?.focus();
    } else {
      otpRefs.current[5]?.focus();
    }
  };

  const handleRecaptchaChange = (token: string | null) => {
    setRecaptchaToken(token);
    setErrors((prev) => ({ ...prev, mobileNumber: token ? undefined : prev.mobileNumber }));
  };

  const notify = (msg: string = "Operation successful!") =>
    toast.success(msg, {
      style: { border: "1px solid #00b852", padding: "16px", color: "#00b852", zIndex: "10000" },
      iconTheme: { primary: "#00b852", secondary: "#FFFAEE" },
    });

  const notifyError = (msg: string) =>
    toast.error(msg, {
      style: { border: "1px solid #cc0000", padding: "16px", color: "#cc0000", zIndex: "10000" },
      iconTheme: { primary: "#cc0000", secondary: "#FFFAEE" },
    });

  const searchParams = useSearchParams();
  const queryParams = Object.fromEntries(searchParams);


  const trackingParams = {
    ref: searchParams.get("ref") || undefined,
    utm_source: searchParams.get("utm_source") || undefined,
    utm_medium: searchParams.get("utm_medium") || undefined,
    utm_content: searchParams.get("utm_content") || undefined,
    utm_campaign: searchParams.get("utm_campaign") || undefined, // optional bonus
    utm_term: searchParams.get("utm_term") || undefined,         // optional
  };

  // console.log("trackingParams",trackingParams)

  const cleanTrackingParams = Object.fromEntries(
    Object.entries(trackingParams).filter(([_, value]) => value !== undefined)
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (formData.honeypot) return;
    if (!validateForm()) {
      if (errors.mobileNumber === "Please complete the reCAPTCHA") {
        notifyError("Please complete the reCAPTCHA");
      }
      return;
    }
    setIsSubmitting(true);

    try {
      if (step === "account") {
        const registrationPayload = {
          screen: 1,
          name: formData.fullName,
          business_name: formData.businessName,
          email: formData.email,
          mobile: formData.mobileNumber ? `${formData.countryCode}${formData.mobileNumber}` : undefined,
          country_id: formData.country,
          ...(queryParams.ref && { ref: String(queryParams.ref).trim() }),
          ...cleanTrackingParams,
        };
        const encryptedData = await aesEncrypt(JSON.stringify(registrationPayload), key);
        const payload = {
          "g-recaptcha-response": recaptchaToken,
          payload: encryptedData,
        };
        const response = await axios.post(
          getApiUrl("/customer/v1/register"),
          payload,
          { headers: { "Content-Type": "application/json" } }
        );
        if (response.data.code === 200) {
          // Details captured and verification started — a lead, not yet a
          // customer. CompleteRegistration fires only once the account exists.
          trackEvent(
            'Lead',
            { content_name: 'Signup started', content_category: 'registration' },
            {
              em: formData.email,
              ph: `${formData.countryCode || ''}${formData.mobileNumber || ''}`,
              fn: formData.fullName,
            },
          );
          toast.dismiss();
          notify("OTP sent successfully!");
          setFormData((prev) => ({
            ...prev,
            reset_token: response.data.data?.reset_token || "",
          }));
          setStep("otp");
        } else {
          notifyError(response.data.message || "Registration failed.");
        }
      } else if (step === "otp") {
        const otpPayload = {
          screen: 2,
          mobile: formData.mobileNumber ? `${formData.countryCode}${formData.mobileNumber}` : undefined,
          otp: formData.otp.join(""),
          reset_token: formData.reset_token,
          ...(queryParams.ref && { ref: String(queryParams.ref).trim() }),
          ...cleanTrackingParams,
        };
        const encryptedData = await aesEncrypt(JSON.stringify(otpPayload), key);
        const payload = { payload: encryptedData };
        const response = await axios.post(
          getApiUrl("/customer/v1/register"),
          payload,
          { headers: { "Content-Type": "application/json" } }
        );
        if (response.data.code === 200) {
          toast.dismiss();
          notify("OTP verified successfully!");
          setFormData((prev) => ({
            ...prev,
            reset_token: response.data.data?.reset_token || "",
          }));
          setStep("password");
        } else {
          notifyError(response.data.message || "OTP verification failed.");
        }
      } else if (step === "password") {
        const passwordPayload = {
          screen: 3,
          mobile: formData.mobileNumber ? `${formData.countryCode}${formData.mobileNumber}` : undefined,
          password: formData.password,
          reset_token: formData.reset_token,
          ...(queryParams.ref && { ref: String(queryParams.ref).trim() }),
          ...cleanTrackingParams,
        };
        const encryptedData = await aesEncrypt(JSON.stringify(passwordPayload), key);
        const payload = { payload: encryptedData };
        const response = await axios.post(
          getApiUrl("/customer/v1/register"),
          payload,
          { headers: { "Content-Type": "application/json" } }
        );
        if (response.data.code === 200) {
          trackEvent(
            'CompleteRegistration',
            { content_name: 'Account created', content_category: 'registration', status: 'completed' },
            {
              em: formData.email,
              ph: `${formData.countryCode || ''}${formData.mobileNumber || ''}`,
              fn: formData.fullName,
            },
          );
          toast.dismiss();
          notify("Account created successfully!");
          const decryptedData = await aesDecrypt(response.data.payload, key);
          const authTokenObj = JSON.parse(decryptedData);

          const [firstName, ...lastNameParts] = formData.fullName.trim().split(" ");
          const lastName = lastNameParts.join(" ") || "";

          login({
            id: authTokenObj.user_id || "",
            username: `${firstName || ""} ${lastName || ""}`.trim() || "Guest",
            email: formData.email || "",
            token: authTokenObj.token || "",
            mobile: formData.mobileNumber || "",
          });

          localStorage.setItem("authToken", authTokenObj.token);
          router.push("/portal");
        } else {
          notifyError(response.data.message || "Password creation failed.");
        }
      }
    } catch (err: any) {
      console.error(`Error in ${step} step:`, err);
      let errorMessage = err.response?.data?.message || "An unexpected error occurred";
      const errorData = err.response?.data;
      if (errorData?.errors && typeof errorData.errors === "object") {
        const fieldErrors = Object.keys(errorData.errors).filter(
          (key) => Array.isArray(errorData.errors[key]) && errorData.errors[key].length > 0
        );
        if (fieldErrors.length > 0) {
          errorMessage = errorData.errors[fieldErrors[0]][0];
        }
      }
      notifyError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen desktop-bg md:bg-[#DFF0FF]">
      <div className="flex justify-center bg-white py-[6px]">
        <Link href="/">
          <Image src={vLogo} alt="vLogo" className="w-[130px]" width={130} height={40} />
        </Link>
      </div>
      <div className="flex justify-center min-h-[90vh] my-[40px] items-start md:items-center md:px-4 lg:px-0">
        <div className="w-full max-w-[918px]">
          <div className="grid md:grid-cols-2 gap-0 bg-white md:rounded-2xl md:shadow-[0px_292px_82px_rgba(6,58,140,0),0px_187px_75px_rgba(6,58,140,0.01),0px_105px_63px_rgba(6,58,140,0.05),0px_47px_47px_rgba(6,58,140,0.09),0px_12px_26px_rgba(6,58,140,0.10)] overflow-hidden">
            <div className="p-6 lg:p-[32px] md:pb-[24px]">
              <div className="max-w-md mx-auto flex flex-col md:h-full h-[80vh] overflow-y-scroll scrollbar-hide justify-between">
                <form onSubmit={handleSubmit} className="space-y-[18px] px-1" onFocus={() => setFormFocused(true)}>
                  {step === "account" && (
                    <>
                      <div className="text-center mb-8">
                        <h1 className="text-[#002F81] text-center text-[24px] font-normal leading-[100%] font-NeueMontreal mb-2">
                          Create Your Account
                        </h1>
                        <p className="text-[#6C6F87] text-[13px] font-normal leading-[140%] schibsted-grotesk">
                          Just a few clicks to begin your journey with us.
                        </p>
                      </div>
                      <input
                        type="text"
                        name="honeypot"
                        value={formData.honeypot}
                        onChange={(e) => handleInputChange("honeypot", e.target.value)}
                        className="hidden"
                        tabIndex={-1}
                        autoComplete="off"
                      />
                      <div>
                        <label
                          htmlFor="fullName"
                          className="text-[#090D28] text-[14px] font-[500] leading-[100%] schibsted-grotesk mb-[5px]"
                        >
                          Full name <span style={{ color: "#EE3535" }}>*</span>
                        </label>
                        <div className="relative">
                          <input
                            id="fullName"
                            type="text"
                            value={formData.fullName}
                            onChange={(e) => handleInputChange("fullName", e.target.value)}
                            placeholder="Enter Name"
                            className={`
                              w-full h-[42px] px-[16px] py-2.5 rounded-[8px] border border-[#D1D7DD] bg-white text-[#090D28]
                              schibsted-grotesk text-base font-normal leading-[120%]
                              outline-none transition-colors duration-200
                              placeholder:text-[#6C6F87] placeholder:schibsted-grotesk
                              placeholder:text-base placeholder:font-normal placeholder:leading-[100%]
                              focus:border-[#2563eb] focus:ring-2 focus:ring-[#2563eb]/10
                              ${errors.fullName ? "border-[#EE3535] focus:border-[#EE3535] focus:ring-[#EF4444]/10" : ""}
                            `}
                          />
                          {errors.fullName && (
                            <div className="text-[#EE3535] absolute top-3 right-3 text-[14px] ">
                              <AlertCircle className="w-4 h-4 mr-1" />
                            </div>
                          )}
                        </div>
                        {errors.fullName && (
                          <div className="flex items-center text-[#EE3535] text-[14px] mt-[4px] schibsted-grotesk">
                            {errors.fullName}
                          </div>
                        )}
                      </div>
                      <div>
                        <label
                          htmlFor="mobileNumber"
                          className="text-[#090D28] text-[14px] font-[500] leading-[100%] schibsted-grotesk mb-[5px]"
                        >
                          Mobile number <span style={{ color: "#EE3535" }}>*</span>
                        </label>
                        <div className="relative flex items-center">
                          <div className="relative">
                            <Select
                              name="country"
                              instanceId="country-code-select"
                              options={customOptions}
                              value={customOptions.find((option) => option.value === formData.country) || null}
                              onChange={handleCountryChange}
                              formatOptionLabel={formatOptionLabel}
                              styles={countryCodeStyles}
                              placeholder="..."
                              isDisabled={isCountryLoading}
                              isLoading={isCountryLoading}
                              aria-label="Select country code"
                            />
                            <svg
                              className="absolute top-1/2 right-1 transform -translate-y-1/2"
                              xmlns="http://www.w3.org/2000/svg"
                              width="16"
                              height="16"
                              viewBox="0 0 16 16"
                              fill="none"
                            >
                              <path
                                d="M4 6L8 10L12 6"
                                stroke="black"
                                strokeWidth="1.25"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                          </div>
                          <input
                            id="mobileNumber"
                            type="text"
                            value={formData.mobileNumber}
                            onChange={(e) => {
                              const maxLen = countries.find((c) => c.id.toString() === formData.country)?.phone_length || 10;
                              handleInputChange("mobileNumber", e.target.value.replace(/\D/g, '').slice(0, maxLen));
                            }}
                            placeholder="Enter mobile number"
                            inputMode="numeric"
                            maxLength={countries.find((c) => c.id.toString() === formData.country)?.phone_length || 10}
                            className={`
                              w-full h-[42px] px-[16px] py-2.5 rounded-r-[8px] border border-[#D1D7DD] bg-white text-[#090D28]
                              schibsted-grotesk text-base font-normal leading-[120%]
                              outline-none transition-colors duration-200
                              placeholder:text-[#6C6F87] placeholder:schibsted-grotesk
                              placeholder:text-base placeholder:font-normal placeholder:leading-[100%]
                              focus:border-[#2563eb] focus:border focus:border-l-1 focus:ring-2 focus:ring-[#2563eb]/10
                              ${errors.mobileNumber || errors.country ? "border border-l-1 border-[#EE3535] focus:border-[#EE3535] focus:ring-[#EF4444]/10" : "border-l-0 focus:border-l-[#2563eb]"}
                            `}
                          />
                          {(errors.mobileNumber || errors.country) && (
                            <div className="text-[#EE3535] absolute top-3 right-3 text-[14px]">
                              <AlertCircle className="w-4 h-4 mr-1" />
                            </div>
                          )}
                        </div>
                        {(errors.mobileNumber || errors.country) && (
                          <div className="flex items-center text-[#EE3535] text-[14px] mt-[4px]">
                            {errors.mobileNumber || errors.country}
                          </div>
                        )}
                      </div>
                      <div>
                        <label
                          htmlFor="email"
                          className="text-[#090D28] text-[14px] font-[500] leading-[100%] schibsted-grotesk mb-[5px]"
                        >
                          Email <span style={{ color: "#EE3535" }}>*</span>
                        </label>
                        <div className="relative">
                          <input
                            id="email"
                            type="text"
                            value={formData.email}
                            onChange={(e) => handleInputChange("email", e.target.value)}
                            placeholder="Enter Email"
                            className={`
                              w-full h-[42px] px-[16px] py-2.5 rounded-[8px] border border-[#D1D7DD] bg-white text-[#090D28]
                              schibsted-grotesk text-base font-normal leading-[120%]
                              outline-none transition-colors duration-200
                              placeholder:text-[#6C6F87] placeholder:schibsted-grotesk
                              placeholder:text-base placeholder:font-normal placeholder:leading-[100%]
                              focus:border-[#2563eb] focus:ring-2 focus:ring-[#2563eb]/10
                              ${errors.email ? "border-[#EE3535] focus:border-[#EE3535] focus:ring-[#EF4444]/10" : ""}
                            `}
                          />
                          {errors.email && (
                            <div className="text-[#EE3535] absolute top-3 right-3 text-[14px]">
                              <AlertCircle className="w-4 h-4 mr-1" />
                            </div>
                          )}
                        </div>
                        {errors.email && (
                          <div className="flex items-center text-[#EE3535] text-[14px] mt-[4px]">
                            {errors.email}
                          </div>
                        )}
                      </div>
                      <div>
                        <label
                          htmlFor="businessName"
                          className="text-[#090D28] text-[14px] font-[500] leading-[100%] schibsted-grotesk mb-[5px]"
                        >
                          Business Name <span style={{ color: "#EE3535" }}>*</span>
                        </label>
                        <div className="relative">
                          <input
                            id="businessName"
                            type="text"
                            value={formData.businessName}
                            onChange={(e) => handleInputChange("businessName", e.target.value)}
                            placeholder="Enter Business Name"
                            className={`
                              w-full h-[42px] px-[16px] py-2.5 rounded-[8px] border border-[#D1D7DD] bg-white text-[#090D28]
                              schibsted-grotesk text-base font-normal leading-[120%]
                              outline-none transition-colors duration-200
                              placeholder:text-[#6C6F87] placeholder:schibsted-grotesk
                              placeholder:text-base placeholder:font-normal placeholder:leading-[100%]
                              focus:border-[#2563eb] focus:ring-2 focus:ring-[#2563eb]/10
                              ${errors.businessName ? "border-[#EE3535] focus:border-[#EE3535] focus:ring-[#EF4444]/10" : ""}
                            `}
                          />
                          {errors.businessName && (
                            <div className="text-[#EE3535] absolute top-3 right-3 text-[14px]">
                              <AlertCircle className="w-4 h-4 mr-1" />
                            </div>
                          )}
                        </div>
                        {errors.businessName && (
                          <div className="flex items-center text-[#EE3535] text-[14px] mt-[4px]">
                            {errors.businessName}
                          </div>
                        )}
                      </div>

                      <div className="recaptcha-container">
                        <div className="relative w-full h-[59px] min-h-[59px] min-[360px]:h-[67px] min-[360px]:min-h-[67px] min-[410px]:h-[78px] min-[410px]:min-h-[78px] overflow-hidden">
                          <div className="absolute top-0 left-0 origin-top-left scale-[0.75] min-[360px]:scale-[0.85] min-[410px]:scale-100">
                            {formFocused ? (
                              <ReCAPTCHA sitekey={recaptchaSiteKey} onChange={handleRecaptchaChange} />
                            ) : (
                              <div
                                className="h-[78px] w-[304px] bg-[#3d3c4e] border border-gray-600 rounded flex items-center justify-center cursor-pointer"
                                onClick={() => setFormFocused(true)}
                              >
                                <span className="text-white schibsted-grotesk text-sm">Click to verify you're human</span>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                      <button
                        type="submit"
                        className="w-full h-[42px] px-5 rounded-[22px] bg-[#195AFF] text-white schibsted-grotesk text-base font-normal flex items-center justify-center leading-[120%] disabled:opacity-60 disabled:cursor-not-allowed"
                        disabled={isSubmitting}
                      >
                        {isSubmitting ? (
                          <div className="flex items-center text-center">
                            <svg
                              className="animate-spin h-5 w-5 mr-2 text-white"
                              xmlns="http://www.w3.org/2000/svg"
                              fill="none"
                              viewBox="0 0 24 24"
                            >
                              <circle
                                className="opacity-25"
                                cx="12"
                                cy="12"
                                r="10"
                                stroke="currentColor"
                                strokeWidth="4"
                              ></circle>
                              <path
                                className="opacity-75"
                                fill="currentColor"
                                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                              ></path>
                            </svg>
                            Creating Account...
                          </div>
                        ) : (
                          "Submit & Verify"
                        )}
                      </button>
                    </>
                  )}
                  {step === "otp" && (
                    <>
                      <div className="text-center mb-8">
                        <h1 className="text-[#002F81] text-2xl font-bold mb-2 font-NeueMontreal">We Just Sent an OTP</h1>
                        <p className="text-[#6C6F87] text-sm mb-4 schibsted-grotesk">
                          Just a quick check to confirm your request, enter the code sent to your{" "}
                          <span className="text-[#4B4F66] schibsted-grotesk text-[13px] not-italic font-normal leading-[140%]">
                            {formData.countryCode}
                            {formData.mobileNumber || "98xxxxx21"}
                          </span>
                        </p>
                      </div>
                      <label
                        htmlFor="otp"
                        className="text-[#090D28] schibsted-grotesk text-sm font-medium mb-1 flex items-center"
                      >
                        OTP <span className="text-[#EE3535] ml-1">*</span>
                      </label>
                      <div className="flex gap-[16px] justify-start">
                        {formData.otp.map((digit, index) => (
                          <div key={index} className="relative">
                            <input
                              ref={(el) => {
                                otpRefs.current[index] = el;
                              }}
                              type="text"
                              value={digit}
                              onChange={(e) => handleInputChange("otp", e.target.value, index)}
                              onKeyDown={(e) => handleKeyDown(e, index)}
                              onPaste={(e) => handlePaste(e, index)}
                              maxLength={1}
                              id={`otp-${index + 1}`}
                              className={`
                                h-[42px] w-[45px] flex justify-center items-center rounded-[8px] border border-[#D1D7DD] bg-white
                                schibsted-grotesk text-base font-normal leading-[120%] outline-none transition-colors duration-200
                                placeholder:text-[#6C6F87] placeholder:schibsted-grotesk placeholder:text-base placeholder:font-normal placeholder:leading-[100%]
                                focus:border-[#2563eb] focus:ring-2 focus:ring-[#2563eb]/10 text-center
                                ${errors.otp ? "border-[#EE3535] focus:border-[#EE3535] focus:ring-[#EF4444]/10" : ""}
                                text-transparent caret-[#090D28] relative
                              `}
                              style={{ fontFamily: '"LatinkaRegular", sans-serif' }}
                              aria-label={`OTP digit ${index + 1}`}
                              aria-invalid={!!errors.otp}
                              aria-describedby={errors.otp ? "otp-error" : undefined}
                            />
                            {digit && (
                              <span
                                className="absolute inset-0 flex items-center justify-center pointer-events-none"
                                style={{
                                  width: "6px",
                                  height: "6px",
                                  aspectRatio: "1/1",
                                  backgroundColor: "#6C6F87",
                                  borderRadius: "50%",
                                  top: "50%",
                                  left: "50%",
                                  transform: "translate(-50%, -50%)",
                                }}
                              ></span>
                            )}
                          </div>
                        ))}
                      </div>
                      {errors.otp && (
                        <div className="text-[#EE3535] text-sm mt-1 text-center">{errors.otp}</div>
                      )}
                      <button
                        type="submit"
                        disabled={isSubmitting}
                        className="w-full h-[42px] px-5 rounded-[22px] bg-[#195AFF] text-white font-LatinkaSemiBold text-base font-normal flex items-center justify-center leading-[120%] disabled:opacity-60 disabled:cursor-not-allowed"
                      >
                        {isSubmitting ? (
                          <div className="flex items-center text-center">
                            <svg
                              className="animate-spin h-5 w-5 mr-2 text-white"
                              xmlns="http://www.w3.org/2000/svg"
                              fill="none"
                              viewBox="0 0 24 24"
                            >
                              <circle
                                className="opacity-25"
                                cx="12"
                                cy="12"
                                r="10"
                                stroke="currentColor"
                                strokeWidth="4"
                              ></circle>
                              <path
                                className="opacity-75"
                                fill="currentColor"
                                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                              ></path>
                            </svg>
                            Verifying...
                          </div>
                        ) : (
                          "Confirm & Set Password"
                        )}
                      </button>
                      <p className="text-[#6C6F87] text-sm text-center mt-2 schibsted-grotesk">Resend Code (120 Sec)</p>
                    </>
                  )}
                  {step === "password" && (
                    <>
                      <div className="text-center mb-8">
                        <h1 className="text-[#002F81] text-2xl font-bold mb-2 font-NeueMontreal">Create Password</h1>
                        <p className="text-[#6C6F87] text-sm mb-4">
                          Make it strong. Make it yours. Avoid "password123"
                        </p>
                      </div>
                      <div>
                        <label
                          htmlFor="password"
                          className="text-[#090D28] text-[14px] font-[500] leading-[100%] schibsted-grotesk mb-[5px]"
                        >
                          Create password <span style={{ color: "#EE3535" }}>*</span>
                        </label>
                        <div className="relative">
                          <input
                            id="password"
                            type={showPassword ? "text" : "password"}
                            value={formData.password}
                            onChange={(e) => handleInputChange("password", e.target.value)}
                            placeholder="Enter password"
                            className={`
                              w-full h-[42px] px-[16px] py-2.5 rounded-[8px] border border-[#D1D7DD] bg-white text-[#090D28]
                              schibsted-grotesk text-base font-normal leading-[120%]
                              outline-none transition-colors duration-200
                              placeholder:text-[#6C6F87] placeholder:schibsted-grotesk
                              placeholder:text-base placeholder:font-normal placeholder:leading-[100%]
                              focus:border-[#2563eb] focus:ring-2 focus:ring-[#2563eb]/10
                              ${errors.password ? "border-[#EE3535] focus:border-[#EE3535] focus:ring-[#EF4444]/10" : ""}
                            `}
                          />
                          <button
                            type="button"
                            onClick={() => setShowPassword(!showPassword)}
                            className="absolute top-1/2 right-3 transform -translate-y-1/2"
                          >
                            {showPassword ? (
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="24"
                                height="24"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="#6C6F87"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              >
                                <path d="M2.062 12.348a1 1 0 0 1 0-.696 10.75 10.75 0 0 1 19.876 0 1 1 0 0 1 0 .696 10.75 10.75 0 0 1-19.876 0" />
                                <circle cx="12" cy="12" r="3" />
                              </svg>
                            ) : (
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="24"
                                height="24"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="#6C6F87"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              >
                                <path d="M10.733 5.076a10.744 10.744 0 0 1 11.205 6.575 1 1 0 0 1 0 .696 10.747 10.747 0 0 1-1.444 2.49" />
                                <path d="M14.084 14.158a3 3 0 0 1-4.242-4.242" />
                                <path d="M17.479 17.499a10.75 10.75 0 0 1-15.417-5.151 1 1 0 0 1 0-.696 10.75 10.75 0 0 1 4.446-5.143" />
                                <path d="m2 2 20 20" />
                              </svg>
                            )}
                          </button>
                          {errors.password && (
                            <div className="text-[#EE3535] absolute top-3 right-10 text-[14px]">
                              <AlertCircle className="w-4 h-4 mr-1" />
                            </div>
                          )}
                        </div>
                        {errors.password && (
                          <div className="flex items-center text-[#EE3535] text-[14px] mt-[4px]">
                            {errors.password}
                          </div>
                        )}
                      </div>
                      <div>
                        <label
                          htmlFor="confirmPassword"
                          className="text-[#090D28] text-[14px] font-[500] leading-[100%] schibsted-grotesk mb-[5px]"
                        >
                          Confirm password <span style={{ color: "#EE3535" }}>*</span>
                        </label>
                        <div className="relative">
                          <input
                            id="confirmPassword"
                            type={showConfirmPassword ? "text" : "password"}
                            value={formData.confirmPassword}
                            onChange={(e) => handleInputChange("confirmPassword", e.target.value)}
                            placeholder="Confirm password"
                            className={`
                              w-full h-[42px] px-[16px] py-2.5 rounded-[8px] border border-[#D1D7DD] bg-white text-[#090D28]
                              schibsted-grotesk text-base font-normal leading-[120%]
                              outline-none transition-colors duration-200
                              placeholder:text-[#6C6F87] placeholder:schibsted-grotesk
                              placeholder:text-base placeholder:font-normal placeholder:leading-[100%]
                              focus:border-[#2563eb] focus:ring-2 focus:ring-[#2563eb]/10
                              ${errors.confirmPassword ? "border-[#EE3535] focus:border-[#EE3535] focus:ring-[#EF4444]/10" : ""}
                            `}
                          />
                          <button
                            type="button"
                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                            className="absolute top-1/2 right-3 transform -translate-y-1/2"
                          >
                            {showConfirmPassword ? (
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="24"
                                height="24"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="#6C6F87"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              >
                                <path d="M2.062 12.348a1 1 0 0 1 0-.696 10.75 10.75 0 0 1 19.876 0 1 1 0 0 1 0 .696 10.75 10.75 0 0 1-19.876 0" />
                                <circle cx="12" cy="12" r="3" />
                              </svg>
                            ) : (
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="24"
                                height="24"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="#6C6F87"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              >
                                <path d="M10.733 5.076a10.744 10.744 0 0 1 11.205 6.575 1 1 0 0 1 0 .696 10.747 10.747 0 0 1-1.444 2.49" />
                                <path d="M14.084 14.158a3 3 0 0 1-4.242-4.242" />
                                <path d="M17.479 17.499a10.75 10.75 0 0 1-15.417-5.151 1 1 0 0 1 0-.696 10.75 10.75 0 0 1 4.446-5.143" />
                                <path d="m2 2 20 20" />
                              </svg>
                            )}
                          </button>
                          {errors.confirmPassword && (
                            <div className="text-[#EE3535] absolute top-3 right-10 text-[14px]">
                              <AlertCircle className="w-4 h-4 mr-1" />
                            </div>
                          )}
                        </div>
                        {errors.confirmPassword && (
                          <div className="flex items-center text-[#EE3535] text-[14px] mt-[4px]">
                            {errors.confirmPassword}
                          </div>
                        )}
                      </div>
                      <button
                        type="submit"
                        disabled={isSubmitting}
                        className="w-full h-[42px] px-5 rounded-[22px] bg-[#195AFF] text-white font-LatinkaSemiBold text-base font-normal flex items-center justify-center leading-[120%] disabled:opacity-60 disabled:cursor-not-allowed"
                      >
                        {isSubmitting ? (
                          <div className="flex items-center text-center">
                            <svg
                              className="animate-spin h-5 w-5 mr-2 text-white"
                              xmlns="http://www.w3.org/2000/svg"
                              fill="none"
                              viewBox="0 0 24 24"
                            >
                              <circle
                                className="opacity-25"
                                cx="12"
                                cy="12"
                                r="10"
                                stroke="currentColor"
                                strokeWidth="4"
                              ></circle>
                              <path
                                className="opacity-75"
                                fill="currentColor"
                                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                              ></path>
                            </svg>
                            Creating Account...
                          </div>
                        ) : (
                          "Create Account"
                        )}
                      </button>
                    </>
                  )}
                </form>
                <div>
                  <p className="text-[#6C6F87] m-0 mt-[16px] md:mt-[40px] text-[14px] schibsted-grotesk text-center">
                    By continuing, you agree to our{" "}
                    <Link
                      href="/terms-and-condition"
                      className="text-[#6C6F87] text-[13px] font-normal leading-[100%] underline schibsted-grotesk"
                    >
                      Terms
                    </Link>{" "}
                    &{" "}
                    <Link
                      href="/terms-and-condition"
                      className="text-[#6C6F87] text-[13px] font-normal leading-[100%] underline schibsted-grotesk"
                    >
                      Privacy Policy.
                    </Link>
                  </p>
                  <p className="text-[#686B7E] schibsted-grotesk m-0 text-center text-[16px] mt-[16px] font-[600]">
                    Already have an account?{" "}
                    <Link href="/signin" className="text-[#195AFF] schibsted-grotesk text-[16px] font-[600]">
                      Login Now
                    </Link>
                  </p>
                </div>
              </div>
            </div>
            <div className="hidden md:block h-full py-[30px] px-[48px] !pb-[24px] text-white login-bg">
              <SigninRightSec />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
